import { useMemo } from 'react';
import DataTable from '@/components/DataTable.jsx';
import { useSeasonData } from './useSeasonData.js';
import './MatrixTab.css';

const TOTAL_GWS = 38;

/*
 * Points per player per gameweek.
 *
 * The AngularJS version coloured cells with a ui-grid cellTemplate that
 * reused the fixture-difficulty classes (controllers.js:1203) — ticker_1
 * for 10+, ticker_3 for 4-9, ticker_5 for 3 or fewer. Difficulty and points
 * are different scales, so this uses its own haul/blank tones instead.
 */
function scoreTone(value) {
	if (value === null || value === undefined) return undefined;
	if (value >= 10) return 'haul';
	if (value >= 6) return 'good';
	if (value >= 3) return 'ok';
	if (value > 0) return 'low';
	return 'blank';
}

export default function MatrixTab({ season }) {
	const { data, status, error } = useSeasonData(season);

	const { columns, rows } = useMemo(() => {
		const source = data?.matrix ?? [];
		/* gw_points is keyed by gameweek as a string; flatten it so each
		 * gameweek becomes a sortable column of its own. */
		const flat = source.map((row) => {
			const out = {
				id: row.id,
				web_name: row.web_name,
				team_name: row.team_name,
				position: row.position,
				total_points: row.total_points,
			};
			for (let gw = 1; gw <= TOTAL_GWS; gw += 1) {
				out[`gw${gw}`] = row.gw_points?.[String(gw)] ?? null;
			}
			return out;
		});

		const cols = [
			{ key: 'web_name', label: 'Player', title: 'Player name', strong: true },
			{ key: 'team_name', label: 'Team' },
			{ key: 'position', label: 'Pos', title: 'Position', tone: (v) => v },
			{
				key: 'total_points',
				label: 'Total',
				title: 'Total points',
				numeric: true,
				defaultSort: 'desc',
			},
			...Array.from({ length: TOTAL_GWS }, (_, i) => ({
				key: `gw${i + 1}`,
				label: String(i + 1),
				title: `Gameweek ${i + 1}`,
				numeric: true,
				tone: (v) => scoreTone(v),
				render: (v) => (v === null ? '' : v),
			})),
		];

		return { columns: cols, rows: flat };
	}, [data]);

	if (status === 'loading') {
		return (
			<div className="tab-loading" role="status">
				<span className="spinner" aria-hidden="true" />
				<span>Loading season…</span>
			</div>
		);
	}
	if (status === 'error') {
		return (
			<div className="notice notice--error" role="alert">
				<strong>Could not load that season.</strong>
				<span>{error}</span>
			</div>
		);
	}

	return (
		<div className="matrix-tab">
			<DataTable
				columns={columns}
				rows={rows}
				rowKey={(r) => r.id}
				filterPlaceholder="Filter by player, team or position"
				maxHeight="70vh"
			/>
			<p className="matrix-legend">
				<span className="matrix-swatch" data-score="blank" /> 0
				<span className="matrix-swatch" data-score="low" /> 1–2
				<span className="matrix-swatch" data-score="ok" /> 3–5
				<span className="matrix-swatch" data-score="good" /> 6–9
				<span className="matrix-swatch" data-score="haul" /> 10+
			</p>
		</div>
	);
}
